/**
 * Proactive Monitoring Service
 * 
 * Detects important infrastructure events and notifies COMO AI.
 * Makes the AI assistant feel alive by proactively suggesting analysis.
 * 
 * Features:
 * - CPU spike detection
 * - Memory leak detection
 * - Error rate monitoring
 * - Response time degradation
 * - Alert patterns
 */

export interface MonitoringEvent {
  id: string;
  type: 'cpu_spike' | 'memory_spike' | 'error_spike' | 'slow_response' | 'disk_full' | 'alert_storm';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  metric_value: number;
  threshold: number;
  timestamp: Date;
  tenant_id: string;
  project_id: string;
}

export type EventCallback = (event: MonitoringEvent) => void;

export class ProactiveMonitoringService {
  private static instance: ProactiveMonitoringService;
  private listeners: EventCallback[] = [];
  private monitoringInterval: NodeJS.Timeout | null = null;
  private lastMetrics: any = null;
  private isMonitoring = false;

  private constructor() {}

  static getInstance(): ProactiveMonitoringService {
    if (!this.instance) {
      this.instance = new ProactiveMonitoringService();
    }
    return this.instance;
  }

  /**
   * Start monitoring for important events
   */
  startMonitoring(intervalMs: number = 5000) {
    if (this.isMonitoring) {
      console.log('🔍 Proactive monitoring already running');
      return;
    }

    console.log('🔍 Starting proactive monitoring...');
    this.isMonitoring = true;

    // Check immediately
    this.checkForEvents();

    // Then check periodically
    this.monitoringInterval = setInterval(() => {
      this.checkForEvents();
    }, intervalMs);
  }

  /**
   * Stop monitoring
   */
  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    this.isMonitoring = false;
    console.log('🔍 Stopped proactive monitoring');
  }

  /**
   * Add event listener
   */
  addEventListener(callback: EventCallback) {
    this.listeners.push(callback);
  }

  /**
   * Remove event listener
   */
  removeEventListener(callback: EventCallback) {
    this.listeners = this.listeners.filter(cb => cb !== callback);
  }

  /**
   * Check for important events
   */
  private checkForEvents() {
    try {
      // Get current user context
      const userStr = localStorage.getItem('coremonit_user');
      if (!userStr) return;

      const user = JSON.parse(userStr);
      const tenant_id = user.tenant_id || user.id;
      const project_id = localStorage.getItem('current_project_id') || 'default-project';

      // Get current metrics
      const metricsStr = localStorage.getItem('demo_metrics');
      const currentMetrics = metricsStr ? JSON.parse(metricsStr) : this.getDefaultMetrics();

      // If we have previous metrics, compare for changes
      if (this.lastMetrics) {
        this.detectCPUSpike(currentMetrics, this.lastMetrics, tenant_id, project_id);
        this.detectMemorySpike(currentMetrics, this.lastMetrics, tenant_id, project_id);
        this.detectErrorSpike(currentMetrics, this.lastMetrics, tenant_id, project_id);
        this.detectSlowResponse(currentMetrics, this.lastMetrics, tenant_id, project_id);
        this.detectDiskIssues(currentMetrics, this.lastMetrics, tenant_id, project_id);
      }

      // Store current metrics for next comparison
      this.lastMetrics = currentMetrics;

      // Also check for alert patterns
      this.checkAlertPatterns(tenant_id, project_id);

    } catch (error) {
      console.error('❌ Error checking for events:', error);
    }
  }

  /**
   * Detect CPU spikes
   */
  private detectCPUSpike(current: any, previous: any, tenant_id: string, project_id: string) {
    const cpuIncrease = current.cpu_usage - previous.cpu_usage;
    const threshold = 20; // 20% increase

    if (cpuIncrease >= threshold || current.cpu_usage > 80) {
      const event: MonitoringEvent = {
        id: `cpu-spike-${Date.now()}`,
        type: 'cpu_spike',
        severity: current.cpu_usage > 90 ? 'critical' : 'warning',
        title: '⚠️ CPU Spike Detected',
        message: `CPU usage ${cpuIncrease > 0 ? `jumped by ${cpuIncrease.toFixed(1)}%` : `is at ${current.cpu_usage}%`}. This could impact performance.`,
        metric_value: current.cpu_usage,
        threshold: 80,
        timestamp: new Date(),
        tenant_id,
        project_id
      };

      this.notifyListeners(event);
    }
  }

  /**
   * Detect memory spikes
   */
  private detectMemorySpike(current: any, previous: any, tenant_id: string, project_id: string) {
    const memoryIncrease = current.memory_usage - previous.memory_usage;
    const threshold = 15; // 15% increase

    if (memoryIncrease >= threshold || current.memory_usage > 85) {
      const event: MonitoringEvent = {
        id: `memory-spike-${Date.now()}`,
        type: 'memory_spike',
        severity: current.memory_usage > 90 ? 'critical' : 'warning',
        title: '⚠️ Memory Spike Detected',
        message: `Memory usage ${memoryIncrease > 0 ? `increased by ${memoryIncrease.toFixed(1)}%` : `is at ${current.memory_usage}%`}. Possible memory leak?`,
        metric_value: current.memory_usage,
        threshold: 85,
        timestamp: new Date(),
        tenant_id,
        project_id
      };

      this.notifyListeners(event);
    }
  }

  /**
   * Detect error rate spikes
   */
  private detectErrorSpike(current: any, previous: any, tenant_id: string, project_id: string) {
    const errorIncrease = (current.error_rate - previous.error_rate) * 100;
    const threshold = 1; // 1% increase in error rate

    if (errorIncrease >= threshold || current.error_rate > 0.05) {
      const event: MonitoringEvent = {
        id: `error-spike-${Date.now()}`,
        type: 'error_spike',
        severity: current.error_rate > 0.1 ? 'critical' : 'warning',
        title: '⚠️ Error Rate Spike',
        message: `Error rate increased to ${(current.error_rate * 100).toFixed(2)}%. Investigating...`,
        metric_value: current.error_rate * 100,
        threshold: 5,
        timestamp: new Date(),
        tenant_id,
        project_id
      };

      this.notifyListeners(event);
    }
  }

  /**
   * Detect slow response times
   */
  private detectSlowResponse(current: any, previous: any, tenant_id: string, project_id: string) {
    const responseIncrease = current.response_time - previous.response_time;
    const threshold = 50; // 50ms increase

    if (responseIncrease >= threshold || current.response_time > 500) {
      const event: MonitoringEvent = {
        id: `slow-response-${Date.now()}`,
        type: 'slow_response',
        severity: current.response_time > 1000 ? 'critical' : 'warning',
        title: '⚠️ Slow Response Time',
        message: `Response time increased to ${current.response_time}ms. Users may experience slowness.`,
        metric_value: current.response_time,
        threshold: 500,
        timestamp: new Date(),
        tenant_id,
        project_id
      };

      this.notifyListeners(event);
    }
  }

  /**
   * Detect disk issues
   */
  private detectDiskIssues(current: any, previous: any, tenant_id: string, project_id: string) {
    if (current.disk_usage > 85) {
      const event: MonitoringEvent = {
        id: `disk-full-${Date.now()}`,
        type: 'disk_full',
        severity: current.disk_usage > 95 ? 'critical' : 'warning',
        title: '⚠️ Disk Space Low',
        message: `Disk usage is at ${current.disk_usage}%. Running out of space!`,
        metric_value: current.disk_usage,
        threshold: 85,
        timestamp: new Date(),
        tenant_id,
        project_id
      };

      this.notifyListeners(event);
    }
  }

  /**
   * Check for alert patterns (alert storms)
   */
  private checkAlertPatterns(tenant_id: string, project_id: string) {
    const alertsStr = localStorage.getItem('demo_alerts');
    if (!alertsStr) return;

    try {
      const alerts = JSON.parse(alertsStr);
      const recentAlerts = alerts.filter((a: any) => {
        const alertTime = new Date(a.timestamp).getTime();
        const now = Date.now();
        return now - alertTime < 5 * 60 * 1000; // Last 5 minutes
      });

      if (recentAlerts.length > 5) {
        const event: MonitoringEvent = {
          id: `alert-storm-${Date.now()}`,
          type: 'alert_storm',
          severity: 'critical',
          title: '🚨 Alert Storm Detected',
          message: `${recentAlerts.length} alerts triggered in the last 5 minutes. Something is wrong!`,
          metric_value: recentAlerts.length,
          threshold: 5,
          timestamp: new Date(),
          tenant_id,
          project_id
        };

        this.notifyListeners(event);
      }
    } catch (error) {
      // Ignore parse errors
    }
  }

  /**
   * Notify all listeners of an event
   */
  private notifyListeners(event: MonitoringEvent) {
    console.log('🚨 Proactive Event:', event.title);
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in event listener:', error);
      }
    });
  }

  /**
   * Get default metrics
   */
  private getDefaultMetrics() {
    return {
      cpu_usage: 45,
      memory_usage: 62,
      disk_usage: 38,
      network_traffic: 1250,
      error_rate: 0.01,
      response_time: 120,
      uptime: 99.9
    };
  }

  /**
   * Simulate a test event (for testing)
   */
  simulateEvent(type: MonitoringEvent['type']) {
    const userStr = localStorage.getItem('coremonit_user');
    if (!userStr) return;

    const user = JSON.parse(userStr);
    const tenant_id = user.tenant_id || user.id;
    const project_id = 'default-project';

    const events: Record<MonitoringEvent['type'], MonitoringEvent> = {
      cpu_spike: {
        id: `test-cpu-${Date.now()}`,
        type: 'cpu_spike',
        severity: 'warning',
        title: '⚠️ CPU Spike Detected',
        message: 'CPU usage jumped to 87%. This could impact performance.',
        metric_value: 87,
        threshold: 80,
        timestamp: new Date(),
        tenant_id,
        project_id
      },
      memory_spike: {
        id: `test-memory-${Date.now()}`,
        type: 'memory_spike',
        severity: 'warning',
        title: '⚠️ Memory Spike Detected',
        message: 'Memory usage is at 91%. Possible memory leak?',
        metric_value: 91,
        threshold: 85,
        timestamp: new Date(),
        tenant_id,
        project_id
      },
      error_spike: {
        id: `test-error-${Date.now()}`,
        type: 'error_spike',
        severity: 'critical',
        title: '⚠️ Error Rate Spike',
        message: 'Error rate increased to 8.5%. Investigating...',
        metric_value: 8.5,
        threshold: 5,
        timestamp: new Date(),
        tenant_id,
        project_id
      },
      slow_response: {
        id: `test-response-${Date.now()}`,
        type: 'slow_response',
        severity: 'warning',
        title: '⚠️ Slow Response Time',
        message: 'Response time increased to 850ms. Users may experience slowness.',
        metric_value: 850,
        threshold: 500,
        timestamp: new Date(),
        tenant_id,
        project_id
      },
      disk_full: {
        id: `test-disk-${Date.now()}`,
        type: 'disk_full',
        severity: 'critical',
        title: '⚠️ Disk Space Low',
        message: 'Disk usage is at 94%. Running out of space!',
        metric_value: 94,
        threshold: 85,
        timestamp: new Date(),
        tenant_id,
        project_id
      },
      alert_storm: {
        id: `test-storm-${Date.now()}`,
        type: 'alert_storm',
        severity: 'critical',
        title: '🚨 Alert Storm Detected',
        message: '12 alerts triggered in the last 5 minutes. Something is wrong!',
        metric_value: 12,
        threshold: 5,
        timestamp: new Date(),
        tenant_id,
        project_id
      }
    };

    this.notifyListeners(events[type]);
  }
}
